// ─── Always-on client error reporter → server app_errors store ───────────────
// Posts real browser errors to POST /api/client-errors so they land in the SAME
// unified store as server errors (readable via the MCP tools + Diagnostics page).
//
// Deliberately INDEPENDENT of lib/observability.ts: that pipeline (OpenObserve
// RUM + OTLP) is gated by the server runtime-config and is OFF in local dev,
// which is exactly where errors most need to be visible. This path is always on
// (no telemetry gate), same-origin, and uses sendBeacon — which survives page
// unload AND bypasses the authBridge 401-lock fetch interceptor.

import { APP_VERSION } from './version';
import { useAuthStore } from './authStore';
import { describeUnknownError, isUserActionableErrorCode, type ClientErrorReport } from '@pcp/shared';

const ENDPOINT = '/api/client-errors';
let installed = false;

// Known-benign browser noise that carries no actionable signal — never reported
// (it would just pollute the unified error store). The ResizeObserver "loop"
// warning is spec-benign: the observer merely defers notifications to the next
// animation frame; nothing actually fails. Browsers still surface it as a window
// 'error' event. Add new patterns here as more non-actionable noise is found.
const IGNORED_MESSAGES: RegExp[] = [
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/i,
  // Opaque cross-origin error: browsers mask a script error from another origin as
  // the literal "Script error." with no stack/location (CORS security). It carries
  // zero actionable signal, so never report it.
  /^script error\.?$/i,
  // View Transitions API: a transition being SKIPPED or ABORTED is NORMAL — the
  // navigation still completes, only the animation is dropped. It happens on a
  // hidden/background tab ("aborted because of invalid state"), under reduced-motion,
  // or when a rapid navigation interrupts an in-flight transition ("skipped"). React
  // Router's data-router view transitions reject these promises and they surface as
  // unhandled rejections; they carry no actionable signal.
  /Transition was (?:skipped|aborted)/i,
];

// Cheap client-side dedup so a tight error loop can't spam the endpoint.
const recent = new Map<string, number>();
const WINDOW_MS = 10_000;
function shouldSend(key: string): boolean {
  const now = Date.now();
  if (recent.size > 200) recent.clear();
  const last = recent.get(key) ?? 0;
  if (now - last < WINDOW_MS) return false;
  recent.set(key, now);
  return true;
}

/** Report one client error to the server store. Never throws. */
export function reportClientError(
  error: unknown,
  extra?: { kind?: string; componentStack?: string },
): void {
  try {
    // A promise rejects with ANYTHING — a plain object, a string, a serialized
    // envelope. Describing it here (rather than `String(reason)`, which yields
    // the useless "[object Object]") is what keeps the row actionable, and what
    // keeps the fingerprint able to tell two non-Error rejections apart (b-01994).
    const described = describeUnknownError(error);
    const message = described.message;
    if (!message) return;
    if (IGNORED_MESSAGES.some((re) => re.test(message))) return; // benign browser noise
    // An EXPECTED, user-actionable rejection (the server answered 4xx with a code
    // from USER_ACTIONABLE_ERROR_CODES — "connect your account", "ask IT for Azure
    // DevOps access") is not an application error, so it never belongs in the
    // store. Guarded HERE, not just at the submitErrorMessage call site, so the
    // window.onerror / unhandledrejection hooks below and any direct caller are
    // covered by the same rule (b-01779). Read structurally rather than via
    // `instanceof ApiError` to keep this module free of an edge to lib/api.
    if (isUserActionableErrorCode((error as { code?: unknown } | null)?.code)) return;
    if (!shouldSend(`${extra?.kind ?? ''}:${message}`)) return;

    let userId: string | undefined;
    try {
      userId = useAuthStore.getState().user?.id;
    } catch {
      /* auth store may not be ready */
    }

    const body: ClientErrorReport = {
      message,
      name: described.name,
      stack: described.stack?.slice(0, 16000),
      componentStack: extra?.componentStack?.slice(0, 8000),
      url: location.pathname + location.search,
      releaseVersion: APP_VERSION,
      kind: extra?.kind,
      userId,
    };
    const json = JSON.stringify(body);

    let sent = false;
    try {
      if (navigator.sendBeacon) {
        sent = navigator.sendBeacon(ENDPOINT, new Blob([json], { type: 'application/json' }));
      }
    } catch {
      /* fall through to fetch */
    }
    if (!sent) {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* a logger must never throw */
  }
}

/** Install the global capture hooks. Idempotent; safe to call once at startup. */
export function installClientErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e: ErrorEvent) => {
    reportClientError(e.error ?? e.message, { kind: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    reportClientError(e.reason, { kind: 'unhandledrejection' });
  });
}
